import { openDB } from 'idb';

/**
 * Device storage for working without signal (IndexedDB via `idb`).
 *
 * - `responses`: the last successful answer to each GET, per user, so pages can
 *   show data offline. Keys are "<userId>|<path>"; sign-out clears them.
 * - `outbox`: changes to field records waiting to be sent to POST /api/sync, in
 *   the order they were made. Kept across sign-out, so nothing recorded in the
 *   field is lost if a session ends before it syncs.
 * - `problems`: changes the server didn't apply (conflicts, rejected records),
 *   kept until the user has seen and dismissed them.
 * - `meta`: small key-value settings shared with the service worker, which
 *   can't read localStorage: the signed-in session (for syncing while the app
 *   is closed) and when each user last synced.
 *
 * Works in pages and in the service worker.
 */

const DB_NAME = 'mangrovecollect';
const DB_VERSION = 3;

let dbPromise = null;
const db = () => {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(database, oldVersion) {
        if (oldVersion < 1) database.createObjectStore('responses');
        if (oldVersion < 2) {
          database.createObjectStore('outbox', { keyPath: 'seq', autoIncrement: true }).createIndex('user', 'userId');
          database.createObjectStore('problems', { keyPath: 'id', autoIncrement: true }).createIndex('user', 'userId');
        }
        if (oldVersion < 3) database.createObjectStore('meta');
      },
    }).catch((error) => {
      dbPromise = null;
      throw error;
    });
  }
  return dbPromise;
};

const keyFor = (userId, path) => `${userId}|${path}`;

// Storage problems (private windows, a full disk) must never break reads, so
// the cache helpers swallow errors: the worst case is "no offline copy". The
// outbox helpers do NOT: losing a field record silently would be worse than an
// error the user can see.
const quietly = async (fn, fallback) => {
  try {
    return await fn();
  } catch {
    return fallback;
  }
};

// --- Saved responses -------------------------------------------------------

export const saveResponse = (userId, path, data) =>
  quietly(async () => (await db()).put('responses', { data, savedAt: Date.now() }, keyFor(userId, path)));

/** `{ data, savedAt }` or undefined. */
export const readResponse = (userId, path) =>
  quietly(async () => (await db()).get('responses', keyFor(userId, path)), undefined);

export const clearUserData = (userId) =>
  quietly(async () => {
    const prefix = keyFor(userId, '');
    await (await db()).delete('responses', IDBKeyRange.bound(prefix, `${prefix}￿`));
  });

/** Ask the browser not to evict our storage (granted automatically for installed apps). */
export const requestPersistentStorage = () =>
  quietly(async () => (navigator.storage?.persist ? navigator.storage.persist() : false), false);

// --- Outbox ----------------------------------------------------------------

/** Append an operation; resolves to the stored entry (with its `seq`). */
export const addToOutbox = async (userId, operation) => {
  const entry = { ...operation, userId, queuedAt: Date.now() };
  const seq = await (await db()).add('outbox', entry);
  return { ...entry, seq };
};

/** This user's waiting operations, oldest first. */
export const readOutbox = async (userId) => {
  const entries = await (await db()).getAllFromIndex('outbox', 'user', userId);
  return entries.sort((a, b) => a.seq - b.seq);
};

export const removeFromOutbox = async (seqs) => {
  if (!seqs.length) return;
  const tx = (await db()).transaction('outbox', 'readwrite');
  await Promise.all([...seqs.map((seq) => tx.store.delete(seq)), tx.done]);
};

export const countOutbox = (userId) =>
  quietly(async () => (await db()).countFromIndex('outbox', 'user', userId), 0);

// --- Problems --------------------------------------------------------------

export const addProblems = async (userId, problems) => {
  if (!problems.length) return;
  const tx = (await db()).transaction('problems', 'readwrite');
  await Promise.all([...problems.map((p) => tx.store.add({ ...p, userId, at: Date.now() })), tx.done]);
};

export const readProblems = (userId) =>
  quietly(async () => (await (await db()).getAllFromIndex('problems', 'user', userId)).sort((a, b) => b.at - a.at), []);

export const dismissProblems = (ids) =>
  quietly(async () => {
    const tx = (await db()).transaction('problems', 'readwrite');
    await Promise.all([...ids.map((id) => tx.store.delete(id)), tx.done]);
  });

// --- Meta ------------------------------------------------------------------

export const saveMeta = (key, value) => quietly(async () => (await db()).put('meta', value, key));

export const readMeta = (key) => quietly(async () => (await db()).get('meta', key), undefined);

export const deleteMeta = (key) => quietly(async () => (await db()).delete('meta', key));
